import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Truck,
  Users,
  Route,
  MapPin,
  DollarSign,
  FileText,
  Wallet,
  UserCog,
  ClipboardList,
  ChevronLeft,
  ChevronRight,
  X,
  TrendingUp,
  CreditCard,
  Briefcase,
} from 'lucide-react';
import { useAuthStore } from '../../store/auth.store';
import { UserRole } from '../../types';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  roles?: UserRole[];
}

export const Sidebar: React.FC<SidebarProps> = ({
  isCollapsed,
  onToggle,
  isMobileOpen = false,
  onMobileClose,
}) => {
  const { hasAnyRole } = useAuthStore();

  const navItems: NavItem[] = [
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: <LayoutDashboard size={20} />,
    },
    // Fleet Management
    {
      label: 'Vehicles',
      path: '/fleet/vehicles',
      icon: <Truck size={20} />,
      roles: [UserRole.ADMIN, UserRole.FLEET_MANAGER, UserRole.AUDITOR],
    },
    {
      label: 'Drivers',
      path: '/fleet/drivers',
      icon: <Users size={20} />,
      roles: [UserRole.ADMIN, UserRole.FLEET_MANAGER, UserRole.AUDITOR],
    },
    // Operations
    {
      label: 'Trips',
      path: '/trips',
      icon: <MapPin size={20} />,
    },
    {
      label: 'Contracts',
      path: '/contracts',
      icon: <Briefcase size={20} />,
      roles: [UserRole.ADMIN, UserRole.FLEET_MANAGER, UserRole.FINANCE_OFFICER],
    },
    {
      label: 'Routes',
      path: '/fleet/routes',
      icon: <Route size={20} />,
      roles: [UserRole.ADMIN, UserRole.FLEET_MANAGER, UserRole.AUDITOR],
    },
    // Finance
    {
      label: 'Expenses',
      path: '/vehicles/expenses',
      icon: <CreditCard size={20} />,
      roles: [UserRole.ADMIN, UserRole.FLEET_MANAGER, UserRole.FINANCE_OFFICER],
    },
    {
      label: 'Invoices',
      path: '/finance/invoices',
      icon: <FileText size={20} />,
      roles: [UserRole.ADMIN, UserRole.FINANCE_OFFICER, UserRole.AUDITOR],
    },
    {
      label: 'Payments',
      path: '/finance/payments',
      icon: <Wallet size={20} />,
      roles: [UserRole.ADMIN, UserRole.FINANCE_OFFICER],
    },
    {
      label: 'Payroll',
      path: '/payroll',
      icon: <DollarSign size={20} />,
      roles: [UserRole.ADMIN, UserRole.FINANCE_OFFICER],
    },
    // Reports & Analytics
    {
      label: 'Reports',
      path: '/reports',
      icon: <ClipboardList size={20} />,
      roles: [UserRole.ADMIN, UserRole.FLEET_MANAGER, UserRole.FINANCE_OFFICER, UserRole.AUDITOR],
    },
    {
      label: 'Vehicle ROI',
      path: '/vehicles/roi',
      icon: <TrendingUp size={20} />,
      roles: [UserRole.ADMIN, UserRole.FLEET_MANAGER, UserRole.FINANCE_OFFICER],
    },
    // Admin
    {
      label: 'Users',
      path: '/admin/users',
      icon: <UserCog size={20} />,
      roles: [UserRole.ADMIN],
    },
  ];

  const filteredNavItems = navItems.filter(
    (item) => !item.roles || hasAnyRole(item.roles)
  );

  return (
    <>
      <div
        className={`md:hidden fixed inset-0 z-40 bg-neutral-900/60 backdrop-blur-sm transition-opacity duration-300 ${
          isMobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onMobileClose}
        aria-hidden
      />

      <aside
        className={`bg-gradient-to-b from-primary-900 via-primary-800 to-primary-navy text-white transition-all duration-300 flex flex-col z-50 shadow-elevated
        fixed md:static inset-y-0 left-0
        ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }
        ${isCollapsed ? 'md:w-20 w-72' : 'w-72 md:w-64'}`}
      >
      <div className="p-4 flex items-center justify-between border-b border-white/10">
        {!isCollapsed && (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white p-1 shadow-md">
              <img src="/logo.png" alt="Transport ERP" className="w-full h-full object-contain" />
            </div>
            <div>
              <span className="font-bold text-base">Harcai Transport</span>
              <span className="text-primary-300 text-xs block -mt-0.5">ERP System</span>
            </div>
          </div>
        )}
        {isCollapsed && (
          <div className="w-10 h-10 mx-auto rounded-xl bg-white p-1 shadow-md">
            <img src="/logo.png" alt="Transport ERP" className="w-full h-full object-contain" />
          </div>
        )}
        <button
          onClick={onToggle}
          className="hidden md:inline-flex p-2 hover:bg-white/10 rounded-xl transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
        >
          {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
        <button
          onClick={onMobileClose}
          className="md:hidden p-2 hover:bg-white/10 rounded-xl transition-all duration-200"
        >
          <X size={20} />
        </button>
      </div>

      <nav className="flex-1 py-4 px-2 overflow-y-auto space-y-1">
        {filteredNavItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 ${
                isActive
                  ? 'bg-white/15 text-white shadow-md backdrop-blur-sm'
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
              } ${isCollapsed ? 'justify-center' : ''}`
            }
            title={isCollapsed ? item.label : undefined}
            onClick={onMobileClose}
          >
            <span className={`${isCollapsed ? '' : 'min-w-[20px]'}`}>{item.icon}</span>
            {!isCollapsed && <span className="text-sm font-medium truncate">{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {!isCollapsed && (
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-white/50 text-center">
            v1.0.0 • © 2026 Insticts
          </div>
        </div>
      )}
      </aside>
    </>
  );
};
